import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  memo,
  createContext,
  useContext,
  Component,
} from "react";
import PropTypes from "prop-types";
import { parseISO, subDays, addDays, isAfter, isBefore } from "date-fns";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

// Import cell components
import CarrierOfferAction from "../../CustomizedTable/CustomCells/ActionsCell/ActionsComponents/CarrierOfferAction/CarrierOfferAction";
import LoadBookingAction from "../../CustomizedTable/CustomCells/ActionsCell/ActionsComponents/LoadBookingAction/LoadBookingAction";
import QuickActionsAction from "../../CustomizedTable/CustomCells/ActionsCell/ActionsComponents/QuickActionsAction/QuickActionsAction";
import ShotClockAction from "../../CustomizedTable/CustomCells/ActionsCell/ActionsComponents/ShotClockAction/ShotClockAction";
import PostLoadAction from "../../CustomizedTable/CustomCells/ActionsCell/ActionsComponents/PostLoadAction/PostLoadAction";
import { DefaultCell, TitleCaseCell, UpCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import BrokerageStatusCell from "../../CustomizedTable/CustomCells/BrokerageStatusCell/BrokerageStatusCell";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import ModeWithTempCell from "../../CustomizedTable/CustomCells/ModeWithTempCell/ModeWithTempCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import MilesCell from "../../CustomizedTable/CustomCells/MilesCell/MilesCell";
import WeightCell from "../../CustomizedTable/CustomCells/WeightCell/WeightCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import MaxPayCell from "../../CustomizedTable/CustomCells/MaxPayCell/MaxPayCell";
import OffersCell from "../../CustomizedTable/CustomCells/OffersCell/OffersCell";
import CarrierRepNameCell from "../../CustomizedTable/CustomCells/CarrierRepNameCell/CarrierRepNameCell";
import EtaStatusCell from "../../CustomizedTable/CustomCells/EtaStatusCell/EtaStatusCell";
import NewRowCell from "../../CustomizedTable/CustomCells/NewRowCell/NewRowCell";

// =============================================================================
// CONSTANTS
// =============================================================================
const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 40;
const OVERSCAN_COUNT = 5;
const ACTIONS_WIDTH = 200;
const ANIMATION_DURATION = 150;

const ROW_BG = {
  active: "#bbdefb",
  new: "#c8e6c9",
  even: "#f5f5f5",
  odd: "#ffffff",
};

// =============================================================================
// HELPERS — detect raw API response objects that must never enter the render tree
// =============================================================================
const API_RESPONSE_KEYS = ["rsp", "rspJson"];
const API_RESPONSE_SHAPE_KEYS = ["status", "text", "url"];

function isApiResponse(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  if (React.isValidElement(obj)) return false;

  if (API_RESPONSE_KEYS.some((k) => k in obj)) return true;

  return API_RESPONSE_SHAPE_KEYS.every((k) => k in obj);
}

// =============================================================================
// CSS KEYFRAMES - Inject once into document
// =============================================================================
const ANIMATION_STYLES_ID = "loadboard-v2-animations";

function injectAnimationStyles() {
  if (document.getElementById(ANIMATION_STYLES_ID)) return;

  const styleSheet = document.createElement("style");
  styleSheet.id = ANIMATION_STYLES_ID;
  styleSheet.textContent = `
    @keyframes gridRowFadeIn {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes gridRowFadeInUp {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .grid-row-animate {
      animation: gridRowFadeIn ${ANIMATION_DURATION}ms ease-out forwards;
    }
    .grid-row-animate-up {
      animation: gridRowFadeInUp ${ANIMATION_DURATION}ms ease-out forwards;
    }
    .grid-row-static {
      opacity: 1;
    }
  `;
  document.head.appendChild(styleSheet);
}

// =============================================================================
// ERROR BOUNDARY - Prevents cell errors from crashing the entire grid
// =============================================================================
class CellErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Cell render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <span style={{ color: "#999" }}>-</span>;
    }
    return this.props.children;
  }
}

CellErrorBoundary.propTypes = {
  children: PropTypes.node,
};

// =============================================================================
// ROW ERROR BOUNDARY
// =============================================================================
class RowErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Row render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            position: "absolute",
            left: 0,
            top: this.props.rowIndex * ROW_HEIGHT,
            height: ROW_HEIGHT,
            width: this.props.totalWidth,
            backgroundColor: "#ffebee",
            alignItems: "center",
            padding: "0 8px",
            fontSize: "0.8125rem",
            color: "#c62828",
          }}
        >
          Error loading row
        </div>
      );
    }
    return this.props.children;
  }
}

RowErrorBoundary.propTypes = {
  children: PropTypes.node,
  rowIndex: PropTypes.number,
  totalWidth: PropTypes.number,
};

// =============================================================================
// CONTEXT FOR SHARED STATE
// =============================================================================
const GridContext = createContext(null);

// =============================================================================
// CELL TYPE REGISTRY
// =============================================================================
const CELL_COMPONENTS = {
  status: BrokerageStatusCell,
  loadId: LoadIdCell,
  carrier: CarrierNameCell,
  mode: ModeWithTempCell,
  datetime: DateTimeRangeCell,
  miles: MilesCell,
  weight: WeightCell,
  currency: CurrencyCell,
  maxPay: MaxPayCell,
  offers: OffersCell,
  carrierRep: CarrierRepNameCell,
  etaStatus: EtaStatusCell,
  uppercase: UpCaseCell,
  titlecase: TitleCaseCell,
  default: DefaultCell,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getRowBgColor(rowIndex, newlyAdded, activeRow) {
  if (activeRow) return ROW_BG.active;
  if (newlyAdded) return ROW_BG.new;
  return rowIndex % 2 === 0 ? ROW_BG.even : ROW_BG.odd;
}

const cellAdapterCache = new WeakMap();

function getCellAdapter(load, column, rowIndex, tabValue) {
  let loadCache = cellAdapterCache.get(load);
  if (!loadCache) {
    loadCache = new Map();
    cellAdapterCache.set(load, loadCache);
  }

  const key = `${column.columnName}-${rowIndex}-${tabValue}`;
  let adapter = loadCache.get(key);

  if (!adapter) {
    adapter = {
      value: load[column.columnName],
      row: { original: load, index: rowIndex },
      column: {
        id: column.columnName,
        idKey: column.idKey || "orderId",
        lateDate: column.lateKey,
        stopType: column.stopType,
        originPoint: "load-board",
        tabValue,
      },
    };
    loadCache.set(key, adapter);
  }

  return adapter;
}

function createComparator(orderBy, order, columns) {
  const column = columns.find((col) => col.columnName === orderBy);
  const isNumeric = column && ["miles", "weight", "currency", "offers", "etaStatus"].includes(column.type);
  const isDate = column?.type === "datetime";
  const multiplier = order === "asc" ? 1 : -1;

  return (a, b) => {
    const aVal = a[orderBy];
    const bVal = b[orderBy];

    if (aVal == null || aVal === "") return multiplier;
    if (bVal == null || bVal === "") return -multiplier;

    if (isNumeric) {
      return ((Number(aVal) || 0) - (Number(bVal) || 0)) * multiplier;
    }

    if (isDate) {
      return (new Date(aVal).getTime() - new Date(bVal).getTime()) * multiplier;
    }

    return String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase()) * multiplier;
  };
}

function processLoads(loads, filters, search, orderBy, order, columns, tabValue) {
  if (!loads.length) return loads;

  let result = loads;
  const now = new Date();
  let needsCopy = false;

  for (const filter of filters) {
    const { fieldName, params } = filter;
    if (!params) continue;

    if (!needsCopy) {
      result = [...result];
      needsCopy = true;
    }

    switch (fieldName) {
      case "daysBack": {
        const cutoff = subDays(now, parseInt(params, 10) || 60);
        result = result.filter((load) => {
          const date = load.originSchedArriveEarly ? parseISO(load.originSchedArriveEarly) : null;
          return date ? isAfter(date, cutoff) : true;
        });
        break;
      }
      case "daysOut": {
        const future = addDays(now, parseInt(params, 10) || 30);
        result = result.filter((load) => {
          const date = load.originSchedArriveEarly ? parseISO(load.originSchedArriveEarly) : null;
          return date ? isBefore(date, future) : true;
        });
        break;
      }
      case "brokerageStatus": {
        const statuses = new Set(params.split("||"));
        result = result.filter((load) => statuses.has(load.brokerageStatus));
        break;
      }
      case "etaStatus": {
        const statuses = new Set(params.split("||").map(Number));
        result = result.filter((load) => statuses.has(load.etaStatus));
        break;
      }
      case "distpatcherUserId":
        result = result.filter((load) => load.distpatcherUserId === params);
        break;
      case "destinationZoneName":
        result = result.filter((load) => load.destinationZoneName === params);
        break;
      default: {
        const values = new Set(params.split("||"));
        result = result.filter((load) => values.has(String(load[fieldName])));
      }
    }
  }

  if (search?.length >= 3) {
    if (!needsCopy) {
      result = [...result];
      needsCopy = true;
    }
    const searchLower = search.toLowerCase();
    result = result.filter((load) =>
      Object.values(load).some(
        (val) => val != null && String(val).toLowerCase().includes(searchLower)
      )
    );
  }

  if (tabValue === 1 && !orderBy) {
    if (!needsCopy) result = [...result];
    result = result.filter(
      (load) => load.exceptions !== null || load.etaStatus === 3 || load.etaStatus === 2
    );
    result.sort((a, b) => {
      const etaDiff = (b.etaStatus || 0) - (a.etaStatus || 0);
      if (etaDiff !== 0) return etaDiff;
      const aDate = a.currentStopSchedArriveEarly ? new Date(a.currentStopSchedArriveEarly).getTime() : 0;
      const bDate = b.currentStopSchedArriveEarly ? new Date(b.currentStopSchedArriveEarly).getTime() : 0;
      return aDate - bDate;
    });
  } else if (orderBy) {
    if (!needsCopy) result = [...result];
    result.sort(createComparator(orderBy, order, columns));
  }

  return result;
}

function calculateTotalRowWidth(visibleColumns) {
  return ACTIONS_WIDTH + visibleColumns.reduce((sum, col) => sum + (col.width || 100), 0);
}

// =============================================================================
// ANIMATION HOOK — only animates rows revealed by scrolling
// =============================================================================
function useRowAnimation(start, end, isInitialRender, isScrollingRef) {
  const prevRangeRef = useRef({ start: 0, end: 0 });
  const renderedRowsRef = useRef(new Set());
  const scrollDirectionRef = useRef("down");

  const animationInfo = useMemo(() => {
    const prevRange = prevRangeRef.current;
    const newRows = new Set();

    if (start > prevRange.start) {
      scrollDirectionRef.current = "down";
    } else if (start < prevRange.start) {
      scrollDirectionRef.current = "up";
    }

    // Only animate when range change was caused by scrolling
    if (isScrollingRef.current) {
      for (let i = start; i < end; i++) {
        if (!renderedRowsRef.current.has(i)) {
          newRows.add(i);
        }
      }
    }

    return {
      newRows,
      scrollDirection: scrollDirectionRef.current,
    };
  }, [start, end, isScrollingRef]);

  useEffect(() => {
    prevRangeRef.current = { start, end };
    for (let i = start; i < end; i++) {
      renderedRowsRef.current.add(i);
    }
    const cleanupThreshold = 100;
    for (const rowIndex of renderedRowsRef.current) {
      if (rowIndex < start - cleanupThreshold || rowIndex > end + cleanupThreshold) {
        renderedRowsRef.current.delete(rowIndex);
      }
    }
  }, [start, end]);

  useEffect(() => {
    if (isInitialRender) {
      renderedRowsRef.current.clear();
      prevRangeRef.current = { start: 0, end: 0 };
    }
  }, [isInitialRender]);

  return animationInfo;
}

// =============================================================================
// VIRTUALIZATION HOOK
// =============================================================================
function useVirtualScroll(containerRef, itemCount, itemHeight, onScroll, dependencies = []) {
  const [range, setRange] = useState({ start: 0, end: 50 });
  const rafId = useRef(null);

  const updateRange = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, clientHeight } = container;

    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - OVERSCAN_COUNT);
    const visibleCount = Math.ceil(clientHeight / itemHeight);
    const end = Math.min(itemCount, start + visibleCount + OVERSCAN_COUNT * 2);

    setRange((prev) => {
      if (prev.start === start && prev.end === end) return prev;
      return { start, end };
    });
  }, [containerRef, itemCount, itemHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      onScroll?.();
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      rafId.current = requestAnimationFrame(updateRange);
    };

    const timeoutId = setTimeout(() => {
      updateRange();
    }, 0);

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener("scroll", handleScroll);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [containerRef, updateRange, onScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
    setRange({ start: 0, end: Math.min(itemCount, 50) });
  }, [itemCount, ...dependencies]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updateRange();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, updateRange]);

  return range;
}

// =============================================================================
// CELL COMPONENT
// =============================================================================
const GridCell = memo(function GridCell({ load, column, rowIndex, tabValue }) {
  const { handleTableAction, updateLoadData } = useContext(GridContext);
  const adapter = getCellAdapter(load, column, rowIndex, tabValue);
  const CellComponent = CELL_COMPONENTS[column.type] || CELL_COMPONENTS.default;

  let content;
  switch (column.type) {
    case "loadId":
      content = <CellComponent cell={adapter} actionCallback={handleTableAction} />;
      break;
    case "carrier":
      content = <CellComponent cell={adapter} column={adapter.column} actionCallback={handleTableAction} />;
      break;
    case "maxPay":
    case "carrierRep":
      content = <CellComponent cell={adapter} row={adapter.row} column={adapter.column} updateData={updateLoadData} />;
      break;
    default:
      content = <CellComponent {...adapter} />;
  }

  return (
    <CellErrorBoundary>
      {content}
    </CellErrorBoundary>
  );
}, (prev, next) => (
  prev.load[prev.column.columnName] === next.load[next.column.columnName] &&
  prev.rowIndex === next.rowIndex &&
  prev.column === next.column
));

GridCell.propTypes = {
  load: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  tabValue: PropTypes.number,
};

// =============================================================================
// ACTIONS CELL — reference equality on load, robust API response guards
// =============================================================================
const ActionsCell = memo(function ActionsCell({ load, rowIndex, isAdminUser }) {
  const { handleTableAction, updateLoadData } = useContext(GridContext);
  const [availableToBook, setAvailableToBook] = useState(false);

  const cellData = useMemo(() => ({ row: { original: load } }), [load]);
  const rowData = useMemo(() => ({ original: load, index: rowIndex }), [load, rowIndex]);
  const columnData = useMemo(() => ({ id: "newlyAdded" }), []);
  const rowObj = useMemo(() => ({ original: load, index: rowIndex }), [load, rowIndex]);

  // Guard: prevent raw API response objects from reaching handleTableAction / render tree
  const safeActionCallback = useCallback((...args) => {
    for (let i = 0; i < args.length; i++) {
      if (isApiResponse(args[i])) {
        console.warn(
          `[ActionsCell] Blocked API response object in actionCallback arg[${i}]:`,
          Object.keys(args[i])
        );
        return;
      }
    }
    handleTableAction?.(...args);
  }, [handleTableAction]);

  // Guard: prevent API response from being set into availableToBook state
  const safeSetAvailableToBook = useCallback((val) => {
    if (isApiResponse(val)) {
      console.warn("[ActionsCell] Blocked API response in setAvailableToBook");
      return;
    }
    setAvailableToBook(Boolean(val));
  }, []);

  // Guard: prevent API response from being passed to updateLoadData
  const safeUpdateLoadData = useCallback((...args) => {
    for (const arg of args) {
      if (isApiResponse(arg)) {
        console.warn("[ActionsCell] Blocked API response in updateLoadData");
        return;
      }
    }
    updateLoadData?.(...args);
  }, [updateLoadData]);

  return (
    <CellErrorBoundary>
      <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
        <div style={{ width: 20, minWidth: 20, display: "flex", justifyContent: "center" }}>
          <NewRowCell
            cell={cellData}
            row={rowData}
            column={columnData}
            updateData={safeUpdateLoadData}
          />
        </div>

        {isAdminUser && (
          <CellErrorBoundary>
            <PostLoadAction load={load} active={false} rowId={rowIndex} updateData={safeUpdateLoadData} />
          </CellErrorBoundary>
        )}
        <CellErrorBoundary>
          <CarrierOfferAction load={load} carrierId={load.carrierId} active={false} actionCallback={safeActionCallback} />
        </CellErrorBoundary>
        <CellErrorBoundary>
          <ShotClockAction
            row={rowObj}
            load={load}
            setAvailableToBook={safeSetAvailableToBook}
            active={false}
            actionCallback={safeActionCallback}
          />
        </CellErrorBoundary>
        <CellErrorBoundary>
          <LoadBookingAction
            availableToBook={availableToBook}
            load={load}
            carrierId={load.carrierId}
            active={false}
            originPoint="load-board"
            actionCallback={safeActionCallback}
          />
        </CellErrorBoundary>
        <CellErrorBoundary>
          <QuickActionsAction load={load} active={false} actionCallback={safeActionCallback} />
        </CellErrorBoundary>
      </div>
    </CellErrorBoundary>
  );
}, (prev, next) => (
  prev.load === next.load &&
  prev.rowIndex === next.rowIndex &&
  prev.isAdminUser === next.isAdminUser
));

ActionsCell.propTypes = {
  load: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  isAdminUser: PropTypes.bool,
};

// =============================================================================
// GRID ROW — reference equality on load
// =============================================================================
const GridRow = memo(function GridRow({
  load,
  rowIndex,
  visibleColumns,
  stickyDepth,
  isAdminUser,
  tabValue,
  totalWidth,
  shouldAnimate,
  scrollDirection,
  animationDelay,
}) {
  const bgColor = getRowBgColor(rowIndex, load.newlyAdded, load.activeRow);

  const stickyPositions = useMemo(() => {
    if (stickyDepth === 0) return null;

    const positions = { actions: 0 };
    let left = ACTIONS_WIDTH;

    if (stickyDepth >= 2 && visibleColumns[0]) {
      positions.col0 = left;
      left += visibleColumns[0].width || 100;
    }
    if (stickyDepth >= 3 && visibleColumns[1]) {
      positions.col1 = left;
    }

    return positions;
  }, [stickyDepth, visibleColumns]);

  const baseCellStyle = {
    display: "flex",
    alignItems: "center",
    padding: "0 6px",
    fontSize: "0.8125rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    borderRight: "1px solid #e0e0e0",
    boxSizing: "border-box",
    backgroundColor: bgColor,
    flexShrink: 0,
  };

  const stickyCellStyle = {
    position: "sticky",
    zIndex: 2,
  };

  const animationClass = shouldAnimate
    ? scrollDirection === "up"
      ? "grid-row-animate-up"
      : "grid-row-animate"
    : "grid-row-static";

  const animationStyle = shouldAnimate
    ? { animationDelay: `${animationDelay}ms` }
    : {};

  return (
    <div
      className={animationClass}
      style={{
        display: "flex",
        position: "absolute",
        left: 0,
        top: rowIndex * ROW_HEIGHT,
        height: ROW_HEIGHT,
        width: totalWidth,
        minWidth: totalWidth,
        borderBottom: "1px solid #e0e0e0",
        opacity: shouldAnimate ? 0 : 1,
        ...animationStyle,
      }}
    >
      {/* Actions Cell */}
      <div
        style={{
          ...baseCellStyle,
          width: ACTIONS_WIDTH,
          minWidth: ACTIONS_WIDTH,
          maxWidth: ACTIONS_WIDTH,
          padding: "0 4px",
          ...(stickyDepth > 0 && { ...stickyCellStyle, left: 0 }),
        }}
      >
        <ActionsCell load={load} rowIndex={rowIndex} isAdminUser={isAdminUser} />
      </div>

      {/* Data Cells */}
      {visibleColumns.map((col, colIndex) => {
        const width = col.width || 100;
        const isSticky = stickyPositions && (
          (colIndex === 0 && stickyDepth >= 2) ||
          (colIndex === 1 && stickyDepth >= 3)
        );
        const stickyLeft = isSticky
          ? (colIndex === 0 ? stickyPositions.col0 : stickyPositions.col1)
          : undefined;
        const isMaxPayCell = col.type === "maxPay";

        return (
          <div
            key={col.columnName}
            style={{
              ...baseCellStyle,
              width,
              minWidth: width,
              maxWidth: width,
              ...(isSticky && { ...stickyCellStyle, left: stickyLeft }),
              ...(isMaxPayCell && { paddingTop: 8 }),
            }}
          >
            <GridCell
              load={load}
              column={col}
              rowIndex={rowIndex}
              tabValue={tabValue}
            />
          </div>
        );
      })}
    </div>
  );
}, (prev, next) => {
  // If load reference changed, always re-render
  if (prev.load !== next.load) return false;

  return (
    prev.rowIndex === next.rowIndex &&
    prev.visibleColumns === next.visibleColumns &&
    prev.stickyDepth === next.stickyDepth &&
    prev.isAdminUser === next.isAdminUser &&
    prev.tabValue === next.tabValue &&
    prev.totalWidth === next.totalWidth &&
    prev.shouldAnimate === next.shouldAnimate
  );
});

GridRow.propTypes = {
  load: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  visibleColumns: PropTypes.array.isRequired,
  stickyDepth: PropTypes.number.isRequired,
  isAdminUser: PropTypes.bool,
  tabValue: PropTypes.number,
  totalWidth: PropTypes.number.isRequired,
  shouldAnimate: PropTypes.bool,
  scrollDirection: PropTypes.string,
  animationDelay: PropTypes.number,
};

// =============================================================================
// GRID ROW WITH ERROR BOUNDARY WRAPPER
// =============================================================================
const SafeGridRow = memo(function SafeGridRow(props) {
  return (
    <RowErrorBoundary rowIndex={props.rowIndex} totalWidth={props.totalWidth}>
      <GridRow {...props} />
    </RowErrorBoundary>
  );
});

SafeGridRow.propTypes = GridRow.propTypes;

// =============================================================================
// SORTABLE HEADER CELL
// =============================================================================
const SortableHeaderCell = memo(function SortableHeaderCell({
  column,
  width,
  isSorted,
  order,
  onSort,
  isSticky,
  stickyLeft,
  baseStyle,
  stickyStyle,
}) {
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSort(column.columnName);
    }
  }, [onSort, column.columnName]);

  const handleClick = useCallback(() => {
    onSort(column.columnName);
  }, [onSort, column.columnName]);

  return (
    <div
      role="columnheader"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        ...baseStyle,
        width,
        minWidth: width,
        maxWidth: width,
        ...(isSticky && { ...stickyStyle, left: stickyLeft }),
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
        {column.title}
      </span>
      {isSorted && (
        <span style={{ marginLeft: 4, fontSize: 10 }}>
          {order === "asc" ? "▲" : "▼"}
        </span>
      )}
    </div>
  );
});

SortableHeaderCell.propTypes = {
  column: PropTypes.object.isRequired,
  width: PropTypes.number.isRequired,
  isSorted: PropTypes.bool.isRequired,
  order: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  isSticky: PropTypes.bool,
  stickyLeft: PropTypes.number,
  baseStyle: PropTypes.object.isRequired,
  stickyStyle: PropTypes.object.isRequired,
};

// =============================================================================
// GRID HEADER
// =============================================================================
const GridHeader = memo(function GridHeader({
  visibleColumns,
  stickyDepth,
  orderBy,
  order,
  onSort,
  onCycleSticky,
  totalWidth,
}) {
  const stickyPositions = useMemo(() => {
    if (stickyDepth === 0) return null;

    const positions = { actions: 0 };
    let left = ACTIONS_WIDTH;

    if (stickyDepth >= 2 && visibleColumns[0]) {
      positions.col0 = left;
      left += visibleColumns[0].width || 100;
    }
    if (stickyDepth >= 3 && visibleColumns[1]) {
      positions.col1 = left;
    }

    return positions;
  }, [stickyDepth, visibleColumns]);

  const baseHeaderCellStyle = useMemo(() => ({
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    fontWeight: 600,
    fontSize: "0.8125rem",
    borderRight: "1px solid #ccc",
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: "#fff",
    flexShrink: 0,
    boxSizing: "border-box",
  }), []);

  const stickyHeaderStyle = useMemo(() => ({
    position: "sticky",
    zIndex: 11,
  }), []);

  const handleCycleStickyKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onCycleSticky();
    }
  }, [onCycleSticky]);

  return (
    <div
      role="row"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        backgroundColor: "#fff",
        borderBottom: "2px solid #333",
        height: HEADER_HEIGHT,
        minHeight: HEADER_HEIGHT,
        width: totalWidth,
        minWidth: totalWidth,
      }}
    >
      {/* Actions Header */}
      <div
        role="columnheader"
        style={{
          ...baseHeaderCellStyle,
          width: ACTIONS_WIDTH,
          minWidth: ACTIONS_WIDTH,
          maxWidth: ACTIONS_WIDTH,
          cursor: "default",
          ...(stickyDepth > 0 && { ...stickyHeaderStyle, left: 0 }),
        }}
      >
        <Tooltip title={
          stickyDepth === 0 ? "Pin Actions" :
          stickyDepth === 1 ? "Pin through Status" :
          stickyDepth === 2 ? "Pin through Load" :
          "Unpin all"
        }>
          <IconButton
            size="small"
            onClick={onCycleSticky}
            onKeyDown={handleCycleStickyKeyDown}
            sx={{ p: 0, mr: 0.5 }}
            aria-label="Toggle sticky columns"
          >
            {stickyDepth > 0 ? (
              <PushPinIcon sx={{ fontSize: 14, color: "primary.main" }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            )}
          </IconButton>
        </Tooltip>
        <span>Actions</span>
      </div>

      {/* Column Headers */}
      {visibleColumns.map((col, colIndex) => {
        const width = col.width || 100;
        const isSorted = orderBy === col.columnName;
        const isSticky = stickyPositions && (
          (colIndex === 0 && stickyDepth >= 2) ||
          (colIndex === 1 && stickyDepth >= 3)
        );
        const stickyLeft = isSticky
          ? (colIndex === 0 ? stickyPositions.col0 : stickyPositions.col1)
          : undefined;

        return (
          <SortableHeaderCell
            key={col.columnName}
            column={col}
            width={width}
            isSorted={isSorted}
            order={order}
            onSort={onSort}
            isSticky={isSticky}
            stickyLeft={stickyLeft}
            baseStyle={baseHeaderCellStyle}
            stickyStyle={stickyHeaderStyle}
          />
        );
      })}
    </div>
  );
}, (prev, next) => (
  prev.visibleColumns === next.visibleColumns &&
  prev.stickyDepth === next.stickyDepth &&
  prev.orderBy === next.orderBy &&
  prev.order === next.order &&
  prev.onSort === next.onSort &&
  prev.onCycleSticky === next.onCycleSticky &&
  prev.totalWidth === next.totalWidth
));

GridHeader.propTypes = {
  visibleColumns: PropTypes.array.isRequired,
  stickyDepth: PropTypes.number.isRequired,
  orderBy: PropTypes.string,
  order: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onCycleSticky: PropTypes.func.isRequired,
  totalWidth: PropTypes.number.isRequired,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
function LoadBoardV2({
  isAdminUser,
  tabValue,
  tabLabels,
  isLoading,
  customFilters,
  customColumns,
  search,
  allLoads,
  deleteNewLoads,
  onLoadFieldUpdate,
  handleTableAction,
  setTotalResults,
}) {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [stickyDepth, setStickyDepth] = useState(1);
  const [processedLoads, setProcessedLoads] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const containerRef = useRef(null);
  const processingTimeoutRef = useRef(null);

  // ── Scroll-tracking refs (suppress animation on data refresh) ──
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const markScrolling = useCallback(() => {
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 200);
  }, []);

  // Inject animation styles on mount
  useEffect(() => {
    injectAnimationStyles();
  }, []);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const currentTabId = tabLabels[tabValue]?.customLoadBoardByUserID;

  const visibleColumns = useMemo(() => {
    const sortKey = tabValue === 1 ? "trackingOrder" : "displayOrder";
    return customColumns
      .filter((col) => col.isChecked)
      .sort((a, b) => a[sortKey] - b[sortKey]);
  }, [customColumns, tabValue]);

  const totalWidth = useMemo(() => {
    return calculateTotalRowWidth(visibleColumns);
  }, [visibleColumns]);

  // Process loads
  useEffect(() => {
    setIsProcessing(true);
    setIsInitialRender(true);

    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    const scheduleProcessing = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

    processingTimeoutRef.current = scheduleProcessing(() => {
      const result = processLoads(
        allLoads,
        customFilters,
        search,
        orderBy,
        order,
        customColumns,
        tabValue
      );
      setProcessedLoads(result);
      setIsProcessing(false);
      setTotalResults?.(result.length);

      setTimeout(() => setIsInitialRender(false), 100);
    }, { timeout: 100 });

    return () => {
      if (processingTimeoutRef.current) {
        if (window.cancelIdleCallback) {
          window.cancelIdleCallback(processingTimeoutRef.current);
        } else {
          clearTimeout(processingTimeoutRef.current);
        }
      }
    };
  }, [allLoads, customFilters, search, orderBy, order, customColumns, tabValue, setTotalResults]);

  // Virtual scroll — passes markScrolling so the hook signals scroll-driven changes
  const { start, end } = useVirtualScroll(
    containerRef,
    processedLoads.length,
    ROW_HEIGHT,
    markScrolling,
    [tabValue, search, customFilters]
  );

  // Row animation — receives isScrollingRef to only animate on scroll
  const { newRows, scrollDirection } = useRowAnimation(
    start,
    end,
    isInitialRender,
    isScrollingRef
  );

  const visibleRows = useMemo(() => {
    return processedLoads.slice(start, end);
  }, [processedLoads, start, end]);

  // Load sorting from localStorage
  useEffect(() => {
    if (currentTabId) {
      try {
        const saved = JSON.parse(localStorage.getItem(`sorting-tab-${currentTabId}`));
        if (saved?.[0]) {
          setOrderBy(saved[0].id || "");
          setOrder(saved[0].desc ? "desc" : "asc");
        } else {
          setOrderBy("");
          setOrder("asc");
        }
      } catch {
        setOrderBy("");
        setOrder("asc");
      }
    }
  }, [currentTabId]);

  // Save sorting to localStorage
  useEffect(() => {
    if (currentTabId && orderBy) {
      localStorage.setItem(
        `sorting-tab-${currentTabId}`,
        JSON.stringify([{ id: orderBy, desc: order === "desc" }])
      );
    }
  }, [orderBy, order, currentTabId]);

  const handleSort = useCallback((columnName) => {
    setOrder((prev) => (orderBy === columnName && prev === "asc" ? "desc" : "asc"));
    setOrderBy(columnName);
  }, [orderBy]);

  const cycleSticky = useCallback(() => {
    setStickyDepth((prev) => (prev + 1) % 4);
  }, []);

  const updateLoadData = useCallback((rowIndex, field, value) => {
    // Guard: block API response objects from being stored as field values
    if (isApiResponse(value)) {
      console.warn("[LoadBoardV2] Blocked API response in updateLoadData value");
      return;
    }
    if (isApiResponse(field)) {
      console.warn("[LoadBoardV2] Blocked API response in updateLoadData field");
      return;
    }

    const load = processedLoads[rowIndex];
    if (!load) return;

    setProcessedLoads((prev) =>
      prev.map((l) => (l.orderId === load.orderId ? { ...l, [field]: value } : l))
    );

    if (field === "maxPay") {
      onLoadFieldUpdate?.(load.orderId, field, value);
    }

    deleteNewLoads?.(load.orderId);
  }, [processedLoads, deleteNewLoads, onLoadFieldUpdate]);

  const contextValue = useMemo(() => ({
    handleTableAction,
    updateLoadData,
  }), [handleTableAction, updateLoadData]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 5, minHeight: 200 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  const totalHeight = processedLoads.length * ROW_HEIGHT;

  return (
    <GridContext.Provider value={contextValue}>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #ddd",
          borderRadius: 1,
        }}
      >
        <div
          ref={containerRef}
          style={{
            flex: 1,
            overflow: "auto",
            willChange: "transform",
            overscrollBehavior: "contain",
          }}
        >
          <GridHeader
            visibleColumns={visibleColumns}
            stickyDepth={stickyDepth}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            onCycleSticky={cycleSticky}
            totalWidth={totalWidth}
          />

          <div
            role="rowgroup"
            style={{
              position: "relative",
              height: totalHeight,
              width: totalWidth,
              minWidth: totalWidth,
            }}
          >
            {visibleRows.map((load, idx) => {
              const actualIndex = start + idx;
              const shouldAnimate = newRows.has(actualIndex) && !isInitialRender;

              let animationDelay = 0;
              if (shouldAnimate) {
                const newRowsArray = Array.from(newRows).sort((a, b) =>
                  scrollDirection === "down" ? a - b : b - a
                );
                const positionInNewRows = newRowsArray.indexOf(actualIndex);
                animationDelay = positionInNewRows * 15;
              }

              return (
                <SafeGridRow
                  key={load.orderId}
                  load={load}
                  rowIndex={actualIndex}
                  visibleColumns={visibleColumns}
                  stickyDepth={stickyDepth}
                  isAdminUser={isAdminUser}
                  tabValue={tabValue}
                  totalWidth={totalWidth}
                  shouldAnimate={shouldAnimate}
                  scrollDirection={scrollDirection}
                  animationDelay={animationDelay}
                />
              );
            })}
          </div>
        </div>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderTop: "1px solid #ddd",
            padding: "6px 16px",
            textAlign: "center",
          }}
        >
          {isProcessing ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Processing...
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Showing {Math.min(start + 1, processedLoads.length)}-{Math.min(end, processedLoads.length)} of {processedLoads.length.toLocaleString()} loads
            </Typography>
          )}
        </Box>
      </Box>
    </GridContext.Provider>
  );
}

LoadBoardV2.propTypes = {
  isAdminUser: PropTypes.bool,
  tabValue: PropTypes.number.isRequired,
  tabLabels: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  customFilters: PropTypes.array.isRequired,
  customColumns: PropTypes.array.isRequired,
  search: PropTypes.string.isRequired,
  allLoads: PropTypes.array.isRequired,
  deleteNewLoads: PropTypes.func,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func.isRequired,
  setTotalResults: PropTypes.func,
};

export default memo(LoadBoardV2);